import { useCallback, useState } from 'react';

import { gruppenkennzeichenApi } from '@/api/services/gruppenkennzeichenApi';
import { Button, IconButton, Spinner, TextBox, useDialogService } from '@/components/ui';
import {
  createChinaAppSysGruppenkennzeichen,
  type ChinaAppSysGruppenkennzeichen,
} from '@/types/entities';
import type { EntityTypes } from '@/types/enums';

import { AdminNotice } from '../AdminNotice';
import { useAdminList } from './adminList';

/** Werte eines Feldes — Port von `IwmAdminListGruppenkennzeichen.razor` (REQ-29 §3). */
export interface AdminGruppenkennzeichenListProps {
  entity: EntityTypes;
  /** Anzeigename der Entität, wie ihn die Liste links führt. */
  entityName: string;
  field: string;
  /** Meldet jede Änderung, damit die Liste links ihre Anzahl nachzieht. */
  onChanged: () => void;
}

/** Rumpf des PUT — die Entität trägt keine Navigationsobjekte. */
function body(row: ChinaAppSysGruppenkennzeichen): ChinaAppSysGruppenkennzeichen {
  return { ...row };
}

export function AdminGruppenkennzeichenList(props: AdminGruppenkennzeichenListProps) {
  const { entity, field, onChanged } = props;
  const dialogService = useDialogService();
  const load = useCallback(() => gruppenkennzeichenApi.getByField(entity, field), [entity, field]);
  const { rows, setRows, loading, error, reload } = useAdminList(load);
  // `null` heißt „keine neue Zeile"; eine neue Zeile steht als Entwurf über der Tabelle.
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const replace = (row: ChinaAppSysGruppenkennzeichen) => {
    setRows((current) => current.map((entry) => (entry.Id === row.Id ? row : entry)));
  };

  /**
   * Optimistisch, mit Rückrollen. Die Antwort trägt den neuen `Timestamp`; ohne sie wäre die
   * nächste Änderung an derselben Zeile ein Konflikt.
   */
  const save = async (row: ChinaAppSysGruppenkennzeichen, value: string) => {
    const next = { ...row, Value: value };
    replace(next);
    const result = await gruppenkennzeichenApi.put(row.Id, body(next));
    replace(result ?? row);
    onChanged();
  };

  const create = async (value: string) => {
    setBusy(true);
    await gruppenkennzeichenApi.create({
      ...createChinaAppSysGruppenkennzeichen(),
      Entitiy: entity,
      Fieldname: field,
      Value: value,
    });
    setDraft(null);
    await reload();
    setBusy(false);
    onChanged();
  };

  const remove = async (row: ChinaAppSysGruppenkennzeichen) => {
    const confirmed = await dialogService.confirm(
      `Soll der Wert „${row.Value ?? ''}" gelöscht werden? Bereits gespeicherte Datensätze behalten ihn.`,
      'Wert löschen',
    );
    if (!confirmed) return;
    setBusy(true);
    await gruppenkennzeichenApi.remove(row.Id);
    await reload();
    setBusy(false);
    onChanged();
  };

  const table = () => (
    <div className="iwm-admin-sublist-wrap">
      <table className="iwm-admin-sublist-table">
        <thead>
          <tr>
            <th scope="col">Value</th>
            <th scope="col" className="iwm-admin-sublist-actions">
              <span className="iwm-admin-sr-only">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {draft === null ? null : (
            <tr>
              <td>
                <TextBox
                  className="iwm-admin-cell-field"
                  label="Neuer Wert"
                  value={draft}
                  onChange={(value) => {
                    // Ein leerer Wert legt nichts an; der Entwurf bleibt stehen.
                    if (value.trim() === '') setDraft(value);
                    else void create(value.trim());
                  }}
                />
              </td>
              <td className="iwm-admin-sublist-actions">
                <IconButton
                  iconClass="fa-solid fa-xmark"
                  title="Remove"
                  onClick={() => {
                    setDraft(null);
                  }}
                />
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={row.Id}>
              <td>
                <TextBox
                  className="iwm-admin-cell-field"
                  label={`Value: ${row.Value ?? ''}`}
                  value={row.Value}
                  onChange={(value) => {
                    // Leeren heißt nicht löschen — der Löschknopf tut das, nach Rückfrage.
                    if (value.trim() !== '') void save(row, value.trim());
                  }}
                />
              </td>
              <td className="iwm-admin-sublist-actions">
                <IconButton
                  iconClass="fa-solid fa-xmark"
                  title="Remove"
                  disabled={busy}
                  onClick={() => {
                    void remove(row);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const content = () => {
    if (loading) {
      return (
        <div className="iwm-state-loading">
          <span className="iwm-state-spinner">
            <Spinner size={20} />
          </span>
          Wird geladen …
        </div>
      );
    }
    if (error !== null) {
      return <AdminNotice tone="danger">Die Werte konnten nicht geladen werden.</AdminNotice>;
    }
    return table();
  };

  return (
    <section className="iwm-admin-sublist">
      <div className="iwm-admin-sublist-head">
        <h4 className="iwm-admin-sublist-title">Werte</h4>
        <Button
          content="Add"
          iconClass="fa-solid fa-plus"
          disabled={busy || draft !== null}
          onClick={() => {
            setDraft('');
          }}
        />
      </div>
      {content()}
      <p className="iwm-admin-sublist-hint">
        {`Diese Werte stehen in Grid und Formularen als Auswahl für ${props.entityName} · ${field}. Geöffnete Ansichten übernehmen eine Änderung erst, wenn die Anwendung neu geladen wird.`}
      </p>
    </section>
  );
}

export default AdminGruppenkennzeichenList;
